import {useEffect, useState} from 'react';

import {Box, ButtonBase, InputBase, Stack, Typography} from '@mui/material';

import {API_CONFIG} from '../../contentGenerator/config';
import {apiFetch} from '../apiFetch';
import {DEFAULT_SHOPIFY_MAPPING} from './brandService';

const C = {
    ink: 'var(--cg-text)',
    surface: 'var(--cg-surface)',
    surfaceAlt: 'var(--cg-surface-alt)',
    border: 'var(--cg-border)',
    border2: 'var(--cg-border-strong)',
    textSoft: 'var(--cg-text-soft)',
    textMuted: 'var(--cg-text-muted)',
    teal: '#1D9E75',
    amber: '#BA7517',
    blue: '#185FA5',
    coral: '#D85A30',
};

const RECOMMENDED = {
    title: {key: 'custom.seo_title', label: 'SEO Title'},
    description: {key: 'custom.seo_description', label: 'SEO Description'},
    bullets: {key: 'custom.bullet_points', label: 'Bullet Points'},
};

const FIELD_CONFIGS = [
    {key: 'title', label: 'Title', color: C.blue, nativeLabel: 'product.title', nativeDesc: 'Default Shopify product title'},
    {key: 'description', label: 'Description', color: C.teal, nativeLabel: 'product.body_html', nativeDesc: 'Default Shopify product description'},
    {key: 'bullets', label: 'Bullets', color: C.amber, nativeLabel: 'Embedded in body_html', nativeDesc: 'Stored as list items inside description HTML', isBullets: true},
];

const isValidMetafieldKey = (source) =>
    source && /^[a-z_][a-z0-9_]*\.[a-z_][a-z0-9_]*$/i.test(source) && !source.startsWith('product.');

function FieldMapping({config, mapping, existingDefs, onChange}) {
    const isNative = !mapping.type || mapping.type === 'native' || mapping.type === 'html_list';
    const isMetafield = mapping.type === 'metafield';
    const recommended = RECOMMENDED[config.key];
    const recExists = existingDefs.some((d) => d.key === recommended.key);
    const sourceValid = isNative || isValidMetafieldKey(mapping.source);

    const matchingDefs = existingDefs.filter((d) => {
        if (config.isBullets) return d.type === 'json' || d.type === 'list.single_line_text_field';
        return d.type === 'single_line_text_field' || d.type === 'multi_line_text_field' || d.type === 'rich_text_field';
    });

    const setNative = () => {
        onChange(config.isBullets
            ? {source: 'product.body_html', type: 'html_list'}
            : {source: `product.${config.key === 'title' ? 'title' : 'body_html'}`, type: 'native'});
    };

    const setMetafield = (source) => {
        onChange({source, type: 'metafield'});
    };

    const switchToMetafield = () => {
        setMetafield(recommended.key);
    };

    return (
        <Box sx={{
            padding: '14px',
            borderRadius: '12px',
            border: `1px solid ${isMetafield ? (sourceValid ? `${config.color}33` : `${C.coral}44`) : C.border}`,
            background: isMetafield ? `${config.color}06` : C.surface,
            transition: 'border-color .15s, background .15s',
        }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{marginBottom: '10px'}}>
                <Stack direction="row" alignItems="center" gap="8px">
                    <Box sx={{
                        width: 24, height: 24, borderRadius: '7px',
                        background: `${config.color}14`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 800, color: config.color,
                    }}>
                        {config.label[0]}
                    </Box>
                    <Box>
                        <Typography sx={{fontSize: 13, fontWeight: 700, color: C.ink, lineHeight: 1.2}}>{config.label}</Typography>
                        <Typography sx={{fontSize: 10, color: C.textMuted}}>
                            {isMetafield
                                ? <><span style={{color: C.amber, fontWeight: 600}}>variant level</span> {sourceValid ? `→ ${mapping.source}` : '— select a metafield'}</>
                                : <><span style={{color: C.teal, fontWeight: 600}}>product level</span> · {config.nativeDesc}</>
                            }
                        </Typography>
                    </Box>
                </Stack>
            </Stack>

            <Stack direction="row" gap="6px" sx={{marginBottom: isMetafield ? '8px' : 0}}>
                <ButtonBase onClick={setNative} sx={{
                    padding: '5px 12px', borderRadius: '7px',
                    border: `1px solid ${isNative ? `${config.color}55` : C.border}`,
                    background: isNative ? `${config.color}0c` : 'transparent',
                    fontFamily: 'inherit', fontSize: 11, fontWeight: 600,
                    color: isNative ? config.color : C.textMuted, cursor: 'pointer',
                }}>
                    {config.nativeLabel} <span style={{fontSize: 9, opacity: 0.6, marginLeft: 4}}>product</span>
                </ButtonBase>
                <ButtonBase onClick={switchToMetafield} sx={{
                    padding: '5px 12px', borderRadius: '7px',
                    border: `1px solid ${isMetafield ? `${config.color}55` : C.border}`,
                    background: isMetafield ? `${config.color}0c` : 'transparent',
                    fontFamily: 'inherit', fontSize: 11, fontWeight: 600,
                    color: isMetafield ? config.color : C.textMuted, cursor: 'pointer',
                }}>
                    Metafield <span style={{fontSize: 9, opacity: 0.6, marginLeft: 4}}>variant</span>
                </ButtonBase>
            </Stack>

            {isMetafield && (
                <Box>
                    <Stack direction="row" gap="4px" flexWrap="wrap" sx={{marginBottom: '6px'}}>
                        <ButtonBase
                            onClick={() => setMetafield(recommended.key)}
                            sx={{
                                padding: '4px 10px', borderRadius: '6px',
                                border: `1px solid ${mapping.source === recommended.key ? `${config.color}55` : C.border}`,
                                background: mapping.source === recommended.key ? `${config.color}0c` : 'transparent',
                                fontFamily: 'monospace', fontSize: 10.5, fontWeight: 600,
                                color: mapping.source === recommended.key ? config.color : C.textSoft,
                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
                            }}>
                            {recommended.key}
                            <span style={{
                                fontFamily: 'inherit', fontSize: 9, fontWeight: 600,
                                color: recExists ? C.teal : C.amber,
                                background: recExists ? '#E1F5EE' : '#FAEEDA',
                                padding: '1px 5px', borderRadius: 4,
                            }}>
                                {recExists ? 'exists' : 'will be created'}
                            </span>
                        </ButtonBase>
                        {matchingDefs
                            .filter((d) => d.key !== recommended.key)
                            .map((d) => (
                                <ButtonBase
                                    key={d.key}
                                    onClick={() => setMetafield(d.key)}
                                    sx={{
                                        padding: '4px 10px', borderRadius: '6px',
                                        border: `1px solid ${mapping.source === d.key ? `${config.color}55` : C.border}`,
                                        background: mapping.source === d.key ? `${config.color}0c` : 'transparent',
                                        fontFamily: 'monospace', fontSize: 10.5,
                                        color: mapping.source === d.key ? config.color : C.textSoft,
                                        cursor: 'pointer',
                                    }}>
                                    {d.key}
                                </ButtonBase>
                            ))}
                    </Stack>

                    <InputBase
                        value={mapping.source || ''}
                        onChange={(e) => setMetafield(e.target.value)}
                        placeholder="namespace.key (required)"
                        sx={{
                            width: '100%',
                            fontFamily: 'monospace',
                            fontSize: 11.5,
                            padding: '6px 10px',
                            borderRadius: '7px',
                            border: `1px solid ${!mapping.source ? `${C.coral}66` : sourceValid ? C.border : `${C.coral}66`}`,
                            background: C.surfaceAlt,
                        }}
                    />
                    {!sourceValid && mapping.source && (
                        <Typography sx={{fontSize: 10, color: C.coral, marginTop: '4px'}}>
                            Must be namespace.key format (e.g. custom.seo_title)
                        </Typography>
                    )}
                    {!mapping.source && (
                        <Typography sx={{fontSize: 10, color: C.coral, marginTop: '4px'}}>
                            Required — select a recommended option or enter a custom key
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default function ShopifyMappingEditor({mapping, onChange}) {
    const [existingDefs, setExistingDefs] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`${API_CONFIG.PROXY_URL}/metafield-definitions`);
                if (res.ok) {
                    const data = await res.json();
                    setExistingDefs(data.definitions || []);
                }
            } catch { /* silent */ }
        })();
    }, []);

    const currentMapping = {...DEFAULT_SHOPIFY_MAPPING, ...(mapping || {})};
    const updateField = (key, value) => onChange({...currentMapping, [key]: value});

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
            {FIELD_CONFIGS.map((config) => (
                <FieldMapping
                    key={config.key}
                    config={config}
                    mapping={currentMapping[config.key]}
                    existingDefs={existingDefs}
                    onChange={(v) => updateField(config.key, v)}
                />
            ))}
        </Box>
    );
}
